import { Component, ViewChild, inject, effect } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DynaDocsModelsService } from './dyna-docs-models.service';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DynaDocsModelRepository } from '@/store/dyna-docs-model-state/dyna-docs-model-state.repository';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { getDynaDocsModelDataTableStructure } from './utils/dyna-docs-models-data-table-object';
import { getDynaDocsModelAddDrawerObject } from './utils/dyna-docs-models-add-drawer-object';
import { getDynaDocsModelFormObject } from './utils/dyna-docs-models-form-object';
import { dynaDocsModelSchema } from '@/validations/dyna-docs-model.validation';
import { getDynaDocsModelUpdateDrawerObject } from './utils/dyna-docs-models-update-drawer-object';
import { getDynaDocsModelDeleteObject } from './utils/dyna-docs-models-delete-dialog-object';
import {DynaDocsModel, DynaDocsModelDto, DynaStatus} from '@/types';

@Component({
  selector: 'sm-dyna-docs-models',
  standalone: true,
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent],
  templateUrl: './dyna-docs-models.component.html',
  styleUrl: './dyna-docs-models.component.scss',
})
export class DynaDocsModelsComponent {
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  service = inject(DynaDocsModelsService);
  store = inject(DynaDocsModelRepository);

  breadcrumb = [
    { label: 'sidebar.settings.title', routerLink: ['/settings'] },
    { label: 'settings.dynaDocsModels.breadcrumb.title', routerLink: ['/settings', 'dyna-docs-models'] },
  ];

  @ViewChild('drawerRef') drawerComponent!: DrawerComponent;
  drawer?: DrawerObject;

  @ViewChild('dialogRef') dialogComponent!: DialogComponent;
  dialog?: DialogObject;

  formObject = getDynaDocsModelFormObject({ store: this.store });

  serverQuery = createServerQuery({});
  data: any[] = [];
  totalRecords = 0;
  loading = true;
  dynaDocsModel!: DynaDocsModelDto;

  rowActions: DataTableRowActions = {
    editAction: row => {
      this.getOne(row.dynaDocsModelId);
      this.drawer = getDynaDocsModelUpdateDrawerObject({
        store: this.store,
        formObject: this.formObject,
        action: () => this.update(row.dynaDocsModelId, this.store.get('dto')),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    deleteAction: row => {
      this.dialog = getDynaDocsModelDeleteObject({
        store: this.store,
        action: () => {
          this.remove(row.dynaDocsModelId);
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  dataTableObject: DynamicDataTable = getDynaDocsModelDataTableStructure({
    store: this.store,
    rowActions: this.rowActions,
    createAction: () => {
      this.drawer = getDynaDocsModelAddDrawerObject({
        store: this.store,
        formObject: this.formObject,
        action: () => this.add(),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    enableServerActions: true,
    serverQuery: this.serverQuery,
  });

  constructor() {
    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();
      const search = this.serverQuery.search();
      const sort = sortBy ? `${sortBy},${sortOrder}` : '';
      this.loadData(page, size, sort, search);
    });
  }

  loadData(page = 0, size = 10, sort?: string, search?: string) {
    this.loading = true;
    this.service.getAll({ page, size, sort, search }).subscribe({
      next: res => {
        this.data = res.content;
        this.totalRecords = res.totalElements as any;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.dynaDocsModels.message.error.loading'),
        });
      },
    });
  }

  getOne(id: number) {
      this.service.getOne(id).subscribe({
        next: (data) => {
          this.dynaDocsModel=data;
          this.store.set('dto', data);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.dynaDocsModels.message.error.edited'),
          });
        },
      });
  }

  add() {
    const result = dynaDocsModelSchema.safeParse(this.store.get('dto'));
    if (result.success) {
      this.service.create(this.store.get('dto')).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(this.serverQuery.page(), this.serverQuery.pageSize());
          this.store.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.dynaDocsModels.message.success.added'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.dynaDocsModels.message.error.added'),
          });
        },
      });
    } else {
      this.store.setErrors(result.error.flatten().fieldErrors as any);
    }
  }

  update(id: number, dto: { description: string,status: DynaStatus }) {
    const result = dynaDocsModelSchema.safeParse(dto);
    if (result.success) {
      this.service.update(id, dto).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(this.serverQuery.page(), this.serverQuery.pageSize());
          this.store.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.dynaDocsModels.message.success.edited'),
          });
        },
        error: (err) => {
          this.drawerComponent.toggleVisible();
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('settings.dynaDocsModels.message.error.edited'),
          });
        },
      });
    } else {
      this.store.setErrors(result.error.flatten().fieldErrors as any);
    }
  }

  remove(id: number) {
    this.service.delete(id).subscribe({
      next: (res: any) => {
        if (res?.status === 226) {
          // Treat 226 as an error
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: res.message || this.translate.instant('settings.dynaDocsModels.message.error.deleted'),
          });
        } else {
          // Success case
          this.loadData(this.serverQuery.page(), this.serverQuery.pageSize());
          this.store.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.dynaDocsModels.message.success.deleted'),
          });
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.dynaDocsModels.message.error.deleted'),
        });
      },
    });
  }
}
